import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getKudos } from '../api.js';

export default function View() {
  const { id } = useParams();
  const [kudos, setKudos] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getKudos(id)
      .then((data) => {
        if (!cancelled) setKudos(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load this kudos.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <p className="mt-10 text-center text-navy-700">Loading&hellip;</p>;
  }

  if (error || !kudos) {
    return (
      <section className="mt-10 text-center">
        <div className="card max-w-xl mx-auto">
          <h1 className="text-2xl font-display font-bold mb-2">
            Hmm, we couldn&rsquo;t find that kudos.
          </h1>
          <p className="text-navy-700 mb-4">{error || 'It may have expired or never existed.'}</p>
          <Link to="/" className="btn-primary no-underline">
            Send one of your own
          </Link>
        </div>
      </section>
    );
  }

  const chainTotal = kudos.chain?.totalCount || 1;
  const chainPosition = kudos.chainPosition || 1;

  return (
    <section className="mt-10 space-y-6">
      <div className="card max-w-xl mx-auto">
        <div className="text-sm uppercase tracking-wider text-teal-700 mb-2">
          A kindness for {kudos.recipientName}
        </div>
        <p className="text-xl text-navy leading-relaxed whitespace-pre-wrap mb-6">
          &ldquo;{kudos.message}&rdquo;
        </p>
        <p className="text-sm text-navy-500">
          Sent anonymously. We don&rsquo;t know who.
        </p>
      </div>

      <p className="text-center text-sm text-navy-700">
        {chainTotal === 1 ? (
          <>🌱 You&rsquo;re <strong>#1</strong> &mdash; the start of a brand-new kindness chain.</>
        ) : (
          <>
            🌱 You&rsquo;re <strong>#{chainPosition}</strong> in a chain that has reached{' '}
            <strong>{chainTotal}</strong> {chainTotal === 1 ? 'person' : 'people'} so far.{' '}
            <Link to={`/chain/${kudos.rootKudosId || id}`} className="text-teal-700">
              See the chain
            </Link>
            .
          </>
        )}
      </p>

      <div className="card max-w-xl mx-auto text-center bg-orange-100/40 border border-orange-500/20">
        <h2 className="text-2xl font-display font-bold text-navy mb-2">Pass it on.</h2>
        <p className="text-navy-700 mb-5">
          Who&rsquo;s someone you know that could use a good word today?
          We&rsquo;ll help you write it.
        </p>
        <Link to={`/k/${id}/forward`} className="btn-accent no-underline">
          Send to up to 3 people
        </Link>
        <p className="text-xs text-navy-500 mt-4">
          Not feeling it right now? That&rsquo;s okay &mdash; the kindness was for you. Just keep it.
        </p>
      </div>

      <p className="text-center text-sm text-navy-500">
        <Link to="/" className="text-teal-700">
          Or send a brand-new one of your own
        </Link>
      </p>
    </section>
  );
}
